/**
 * DiscoverPage.jsx — v1.0.0 Gate 1
 * Primary discovery tab: search, filters, category grid, deal results.
 *
 * Two states:
 *   - Results exist → full UI: header, category pills, result count, offer cards
 *   - Zero results  → clean empty state only: centered message + CTA, nothing else
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import { OfferCard } from "@/components/shared/OfferCard";
import {
  Search, Loader2, Utensils, Sandwich, Coffee, CakeSlice,
  IceCreamCone, Gem, Shirt, ShoppingBag, Ticket, PawPrint,
  Sparkles, Wrench, Store, Gift,
} from "lucide-react";
import { trackSearchPerformed, trackFilterApplied } from "@/lib/events";

const CATEGORY_ICONS = {
  "Restaurant": Utensils, "Fast Food": Sandwich, "Cafes & Drinks": Coffee,
  "Bakery": CakeSlice, "Dessert & Ice Cream": IceCreamCone,
  "Beauty & Skincare": Gem, "Clothing & Apparel": Shirt,
  "Retail": ShoppingBag, "Entertainment": Ticket, "Pet": PawPrint,
  "Health & Fitness": Sparkles, "Auto": Wrench, "Home & Garden": Store,
};

export default function DiscoverPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (selectedCategory) params.set("category", selectedCategory);
      const res = await axios.get(`${API}/birthday-offers?${params}`);
      const data = res.data?.offers || res.data || [];
      setOffers(data);
      setTotal(res.data?.total || data.length);
      // v1.0.0 GATE 3 — fire search_performed when user has a query
      if (debouncedSearch && debouncedSearch.length >= 2) {
        trackSearchPerformed(
          debouncedSearch.length,
          selectedCategory ? ["category"] : [],
          res.data?.total || data.length
        );
      }
    } catch {
      setOffers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => setCategories(r.data?.categories || r.data || [])).catch(() => {});
  }, []);

  const toggleCategory = (cat) => {
    setSelectedCategory((prev) => {
      const next = prev === cat ? null : cat;
      if (next) trackFilterApplied("category", next);
      return next;
    });
  };

  const isEmptyState = !loading && offers.length === 0 && (debouncedSearch || selectedCategory);

  return (
    <div className="min-h-screen pb-24" style={{ background: theme.pageBg }} data-testid="discover-page">
      {/* Search bar — always visible */}
      <div className="sticky top-0 z-50 px-4 pt-4 pb-3" style={{ background: theme.pageBg }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search deals, restaurants, stores..."
            className="w-full pl-10 pr-4 py-3 rounded-2xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 shadow-sm"
            data-testid="discover-search-input"
          />
        </div>
      </div>

      {isEmptyState ? (
        /* ═══ EMPTY STATE — zero results — clean, centered, nothing else ═══ */
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="discover-empty-state">
          <div className="text-center px-8">
            <p className="text-base font-semibold text-slate-700 leading-relaxed">
              Couldn't find anything for that… yet.<br />
              Let's explore something delicious instead.
            </p>
            <button
              onClick={() => { setSearch(""); setSelectedCategory(null); }}
              className="mt-6 px-6 py-3 rounded-full text-white text-sm font-semibold"
              style={{ backgroundColor: theme.buttonBg }}
              data-testid="discover-browse-categories-btn"
            >
              Browse Categories
            </button>
          </div>
        </div>
      ) : (
        /* ═══ NORMAL STATE — full UI: header, categories, results ═══ */
        <>
          {/* Category pills */}
          <div className="px-4 mb-3">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
              {categories.filter(c => c !== "All").map((catName) => {
                const isActive = selectedCategory === catName;
                const Icon = CATEGORY_ICONS[catName] || Gift;
                return (
                  <button
                    key={catName}
                    onClick={() => toggleCategory(catName)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${
                      isActive
                        ? "text-white border-transparent"
                        : "bg-white text-slate-600 border-slate-200"
                    }`}
                    style={isActive ? { backgroundColor: theme.primary } : {}}
                    data-testid={`discover-cat-${catName.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {catName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Header + result count */}
          <div className="px-4 mb-3">
            <h2 className="text-lg font-bold text-slate-800" data-testid="discover-header">
              {selectedCategory || "Discover deals near you"}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5" data-testid="discover-result-count">
              {total} {total === 1 ? "deal" : "deals"} available
            </p>
          </div>

          {/* Results */}
          <div className="px-4 space-y-3">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.primary }} />
              </div>
            ) : (
              offers.map((offer, i) => (
                <OfferCard key={offer.offer_id || offer.business_id || i} offer={offer} />
              ))
            )}
          </div>
        </>
      )}

      <BottomNav />
    </div>
  );
}
